import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
    RefreshCw, UserCheck, Clock, UserX, Palmtree, 
    Home, Search, Users, AlertCircle, Coffee
} from 'lucide-react';
import logger from '@/lib/logger';

const STATUS_CONFIG = {
    on_time: { label: 'On Time', color: 'bg-emerald-500', icon: UserCheck, textColor: 'text-emerald-600' },
    late: { label: 'Late', color: 'bg-amber-500', icon: Clock, textColor: 'text-amber-600' },
    not_yet_in: { label: 'Not Yet In', color: 'bg-slate-400', icon: AlertCircle, textColor: 'text-slate-500' },
    on_leave: { label: 'On Leave', color: 'bg-violet-500', icon: Palmtree, textColor: 'text-violet-600' },
    wfh: { label: 'Work From Home', color: 'bg-blue-500', icon: Home, textColor: 'text-blue-600' },
    absent: { label: 'Absent', color: 'bg-red-500', icon: UserX, textColor: 'text-red-600' },
    day_off: { label: 'Day Off', color: 'bg-slate-300', icon: Coffee, textColor: 'text-slate-400' },
};

const EmployeeCard = ({ emp }) => {
    const cfg = STATUS_CONFIG[emp.status] || STATUS_CONFIG.not_yet_in;
    const Icon = cfg.icon;
    
    return (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow" data-testid={`whos-in-card-${emp.employee_code}`}>
            <div className={`w-2 h-full min-h-[40px] rounded-full ${cfg.color}`} />
            <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{emp.name}</p>
                <p className="text-xs text-muted-foreground truncate">{emp.designation || emp.department}</p>
            </div>
            <div className="text-right shrink-0">
                {emp.check_in ? (
                    <div>
                        <p className="text-xs font-mono font-medium">{emp.check_in?.slice(0,5)}</p>
                        {emp.check_out && (
                            <p className="text-xs font-mono text-muted-foreground">{emp.check_out?.slice(0,5)}</p>
                        )}
                    </div>
                ) : (
                    <Icon className={`h-4 w-4 ${cfg.textColor}`} />
                )}
                {emp.late_minutes > 0 && (
                    <p className="text-xs text-amber-600 font-medium">+{emp.late_minutes}m</p>
                )}
            </div>
        </div>
    );
};

const CategorySection = ({ title, employees, config, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    const Icon = config.icon;
    
    if (employees.length === 0) return null;
    
    return (
        <div className="space-y-2">
            <button 
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 w-full text-left group"
                data-testid={`category-${title.toLowerCase().replace(/\s/g, '-')}`}
            >
                <div className={`p-1.5 rounded-md ${config.color} bg-opacity-20`}>
                    <Icon className={`h-4 w-4 ${config.textColor}`} />
                </div>
                <span className="font-semibold text-sm">{title}</span>
                <Badge variant="secondary" className="ml-auto text-xs">{employees.length}</Badge>
                <span className="text-muted-foreground text-xs">{open ? '−' : '+'}</span>
            </button>
            {open && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 pl-1">
                    {employees.map(emp => (
                        <EmployeeCard key={emp.employee_id} emp={emp} />
                    ))}
                </div>
            )}
        </div>
    );
};

const WhosInPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [department, setDepartment] = useState('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [department]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = department && department !== 'all' ? `?department=${department}` : '';
            const res = await api.get(`/hr/whos-in${params}`);
            setData(res.data);
        } catch (error) {
            logger.error('Error:', error);
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };

    const filterBySearch = (employees) => {
        if (!search) return employees;
        const q = search.toLowerCase();
        return employees.filter(e => 
            e.name?.toLowerCase().includes(q) || 
            e.department?.toLowerCase().includes(q) ||
            e.employee_code?.toLowerCase().includes(q)
        );
    };

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="whos-in-loading">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const s = data.summary;
    const cats = data.categories || {};

    return (
        <div className="space-y-5" data-testid="whos-in-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Who's In</h1>
                    <p className="text-sm text-muted-foreground">
                        {data.date} {data.is_holiday && `— ${data.holiday_name || 'Holiday'}`} {data.is_weekend && '— Weekend'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input 
                            placeholder="Search employee..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 w-[200px]"
                            data-testid="whos-in-search"
                        />
                    </div>
                    <Select value={department} onValueChange={setDepartment}>
                        <SelectTrigger className="w-[180px]" data-testid="whos-in-dept-filter">
                            <SelectValue placeholder="All Departments" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Departments</SelectItem>
                            <SelectItem value="Sales">Sales</SelectItem>
                            <SelectItem value="Finance">Finance</SelectItem>
                            <SelectItem value="Customer Service">Customer Service</SelectItem>
                            <SelectItem value="Mentors/Academics">Mentors/Academics</SelectItem>
                            <SelectItem value="Operations">Operations</SelectItem>
                            <SelectItem value="Marketing">Marketing</SelectItem>
                            <SelectItem value="HR">HR</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchData} data-testid="whos-in-refresh">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <Card className="border-t-2 border-t-blue-500">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold">{s.total_employees}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                    </CardContent>
                </Card>
                <Card className="border-t-2 border-t-emerald-500">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold text-emerald-600">{s.on_time}</p>
                        <p className="text-xs text-muted-foreground">On Time</p>
                    </CardContent>
                </Card>
                <Card className="border-t-2 border-t-amber-500">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold text-amber-600">{s.late}</p>
                        <p className="text-xs text-muted-foreground">Late</p>
                    </CardContent>
                </Card>
                <Card className="border-t-2 border-t-blue-400">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold text-blue-500">{s.wfh}</p>
                        <p className="text-xs text-muted-foreground">WFH</p>
                    </CardContent>
                </Card>
                <Card className="border-t-2 border-t-violet-500">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold text-violet-600">{s.on_leave}</p>
                        <p className="text-xs text-muted-foreground">On Leave</p>
                    </CardContent>
                </Card>
                <Card className="border-t-2 border-t-slate-400">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold text-slate-500">{s.not_yet_in}</p>
                        <p className="text-xs text-muted-foreground">Not Yet In</p>
                    </CardContent>
                </Card>
                <Card className="border-t-2 border-t-indigo-500">
                    <CardContent className="pt-4 pb-3 px-4 text-center">
                        <p className="text-2xl font-bold text-indigo-600">{s.attendance_rate}%</p>
                        <p className="text-xs text-muted-foreground">Rate</p>
                    </CardContent>
                </Card>
            </div>

            {/* Department Summary */}
            {data.department_summary && Object.keys(data.department_summary).length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Users className="h-4 w-4" /> Department Overview
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                            {Object.entries(data.department_summary).map(([dept, info]) => (
                                <div key={dept} className="p-3 rounded-lg bg-muted/40 border">
                                    <p className="font-medium text-xs truncate mb-1">{dept}</p>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span className="text-emerald-600 font-medium">{info.present} in</span>
                                        <span className="text-muted-foreground">/</span>
                                        <span>{info.total}</span>
                                        {info.late > 0 && <span className="text-amber-600">({info.late} late)</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Employee Categories */}
            <div className="space-y-4">
                <CategorySection title="On Time" employees={filterBySearch(cats.on_time || [])} config={STATUS_CONFIG.on_time} />
                <CategorySection title="Late" employees={filterBySearch(cats.late || [])} config={STATUS_CONFIG.late} />
                <CategorySection title="Work From Home" employees={filterBySearch(cats.wfh || [])} config={STATUS_CONFIG.wfh} />
                <CategorySection title="Not Yet In" employees={filterBySearch(cats.not_yet_in || [])} config={STATUS_CONFIG.not_yet_in} defaultOpen={false} />
                <CategorySection title="On Leave" employees={filterBySearch(cats.on_leave || [])} config={STATUS_CONFIG.on_leave} defaultOpen={false} />
                <CategorySection title="Absent" employees={filterBySearch(cats.absent || [])} config={STATUS_CONFIG.absent} defaultOpen={false} />
                <CategorySection title="Day Off" employees={filterBySearch(cats.day_off || [])} config={STATUS_CONFIG.day_off} defaultOpen={false} />
            </div>
        </div>
    );
};

export default WhosInPage;
